import React from 'react';

import type { GQL } from '@aline/common';
import { getFileFieldDisplayName } from '@aline/graphql';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { AlineTooltip } from 'src/components';
import { GenericPropertyIcon } from 'src/components/icons/GenericPropertyIcon';
import { makeStyles } from 'tss-react/mui';

import { useReportPlanRowStyles } from './ReportPlanRow';

const useStyles = makeStyles()(theme => ({
  /**
   * One icon for every system column: what they have in common is that the
   * value is already on the document, not a data type the user picks.
   */
  icon: {
    '& > g': { opacity: 1 },
    '& path': { fill: theme.palette.common.black },
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
    fontStyle: 'italic',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
}));

interface ReportPlanFileFieldRowProps {
  field: GQL.ReportColumnFileField;
}

/**
 * One system document column (Document Name, Status, Contract Type, …) on
 * the report plan. Its value is already on the document, so unlike an
 * extraction column there is no question to edit, data type to pick, or
 * approval-card action to take.
 */
export const ReportPlanFileFieldRow = React.memo(function ReportPlanFileFieldRow({
  field,
}: ReportPlanFileFieldRowProps) {
  const { classes } = useStyles();
  const { classes: rowClasses } = useReportPlanRowStyles();

  return (
    <div className={rowClasses.row}>
      <div className={rowClasses.content}>
        <AlineTooltip arrow placement="top" title="System">
          <div className={clsx(rowClasses.typeSlot, classes.icon)}>
            <GenericPropertyIcon />
          </div>
        </AlineTooltip>
        <div className={classes.text}>
          <Typography variant="subtitle2">{getFileFieldDisplayName(field)}</Typography>
          <Typography className={classes.secondaryText} variant="body2">
            System column
          </Typography>
        </div>
      </div>
    </div>
  );
});
